/**
 * Needs authoring — v10.4 Track G (V104-G-7, ADR-0076 §7–§8).
 *
 * A lane in the Editorial queue: laws whose rule draft could not be filed
 * as a grounded proposal. Each card shows the law (with a link to its
 * source), the reason in plain words, the reason's main action, and the
 * other allowed actions as quiet buttons below. A re-run shows "Drafting
 * since …" until the background run files a draft or a new reason; the lane
 * refreshes itself meanwhile. Viewers see the lane read-only. No hashes or
 * internal codes are shown.
 *
 * Three-critic review (V104-G-7): one filled primary per lane, focus moves
 * into a form when it opens and back when it closes, one persistent status
 * region, plain-word field names.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, Checkbox, EmptyState, Field, Input, Spinner, Textarea } from '@aigovops/design-system';
import type { RuleAuthoringAction, RuleAuthoringItemView, RuleAuthoringReason } from '@aigovops/core';
import { actOnAuthoringItem, listAuthoringItems } from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import { jurisdictionName } from '../lib/ai-program-model';
import { TermProposalForm } from './TermProposalForm';

/** V104-G-12: words the drafter used that the jurisdiction has not declared (detail.missing_terms). */
export function missingTerms(item: RuleAuthoringItemView): Array<{ kind: 'role' | 'tier'; term_key: string }> {
  const raw = (item.detail as { missing_terms?: unknown }).missing_terms;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (m): m is { kind: 'role' | 'tier'; term_key: string } =>
      !!m && typeof m === 'object' && ((m as { kind?: unknown }).kind === 'role' || (m as { kind?: unknown }).kind === 'tier') && typeof (m as { term_key?: unknown }).term_key === 'string'
  );
}

/** Missing words not yet filed from this item (detail.proposed_terms), in order. */
export function remainingTerms(item: RuleAuthoringItemView): Array<{ kind: 'role' | 'tier'; term_key: string }> {
  const filed = (item.detail as { proposed_terms?: unknown }).proposed_terms;
  const done = new Set(
    (Array.isArray(filed) ? filed : []).map((f) => `${String((f as { kind?: unknown }).kind)}:${String((f as { term_key?: unknown }).term_key)}`)
  );
  return missingTerms(item).filter((m) => !done.has(`${m.kind}:${m.term_key}`));
}

/**
 * V104-G-12: on a gap that names undeclared words, proposing the word leads and
 * requesting a rule field steps back; once every word is filed, authoring leads.
 */
export function orderedActions(item: RuleAuthoringItemView): readonly RuleAuthoringAction[] {
  const acts = item.actions;
  if (!missingTerms(item).length || !acts.includes('propose_term')) return acts;
  const rest = acts.filter((a) => a !== 'propose_term' && a !== 'request_vocabulary' && a !== 'author_rule');
  return remainingTerms(item).length
    ? ['propose_term', ...acts.filter((a) => a === 'author_rule'), ...rest, ...acts.filter((a) => a === 'request_vocabulary')]
    : [...acts.filter((a) => a === 'author_rule'), ...rest, 'propose_term', ...acts.filter((a) => a === 'request_vocabulary')];
}

const REASON_LABEL: Record<RuleAuthoringReason, string> = {
  provider_error: 'Drafting failed',
  thin_source: 'No usable law text',
  already_covered: 'Already covered',
  vocabulary_gap: 'Needs a new rule field',
  multi_rule_law: 'Needs several rules',
  ungrounded: 'Draft not backed by the text',
};

const ACTION_LABEL: Record<RuleAuthoringAction, string> = {
  retry: 'Try again',
  draft_anyway: 'Draft anyway',
  add_statute_text: 'Add law text',
  link_existing: 'Link existing rules',
  author_rule: 'Author the rule',
  request_vocabulary: 'Request a rule field',
  propose_term: 'Propose the term',
  mark_not_applicable: 'Not applicable',
};

/** "Mark rule written" — the note form for a rule written outside the app (the author_rule action). */
const MARK_WRITTEN = 'Mark rule written';

/** Rules already filed from this item in the authoring form (multi-rule laws). */
export function filedCount(item: RuleAuthoringItemView): number {
  const a = (item.detail as { authored?: unknown }).authored;
  return Array.isArray(a) ? a.length : 0;
}

/** Actions that open a short form first. */
const NEEDS_FORM = new Set<RuleAuthoringAction>(['add_statute_text', 'author_rule', 'request_vocabulary', 'mark_not_applicable', 'link_existing']);
/** Actions that start a background draft. */
const RERUNS = new Set<RuleAuthoringAction>(['retry', 'draft_anyway', 'add_statute_text']);
/** A re-run older than this is treated as finished (a run that died must not lock the card). */
const RERUN_WINDOW_MS = 15 * 60 * 1000;

const MIN_TEXT = 2000;
const MIN_NOTE = 3;

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function humanize(concept: string): string {
  return concept.replace(/_/g, ' ');
}

/** Plain words → the stored field name ("Deepfakes of real people" → deepfakes_of_real_people). */
export function toFieldName(words: string): string {
  return words.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[^a-z]+/, '').slice(0, 64);
}

/** When a re-run was requested and is still plausibly running, else null. */
export function pendingSince(item: RuleAuthoringItemView, now = Date.now()): Date | null {
  const rerun = (item.detail as { rerun?: { requested_at?: string } }).rerun;
  if (!rerun?.requested_at) return null;
  const at = new Date(rerun.requested_at);
  if (Number.isNaN(at.getTime()) || now - at.getTime() > RERUN_WINDOW_MS) return null;
  return at;
}

/** One plain fact from what the pipeline recorded. */
export function describeItem(item: RuleAuthoringItemView): string {
  const d = item.detail as Record<string, unknown>;
  if (item.state === 'blocked_vocabulary') {
    const req = d.vocabulary_request as { concept?: string } | undefined;
    return `Waiting for a rule field: ${humanize(req?.concept ?? 'requested')}.`;
  }
  switch (item.reason_code) {
    case 'provider_error':
      return 'The drafting service didn’t answer.';
    case 'thin_source':
      return d.fetch === 'unsupported_type'
        ? 'The source is a PDF or another file we can’t read.'
        : 'Too little law text at the source.';
    case 'already_covered': {
      const n = item.linked_rule_ids.length;
      return `${n} live ${n === 1 ? 'rule already cites' : 'rules already cite'} this law.`;
    }
    case 'vocabulary_gap': {
      const all = missingTerms(item);
      if (all.length) {
        const left = remainingTerms(item);
        const leftKeys = new Set(left.map((m) => `${m.kind}:${m.term_key}`));
        const filed = all.filter((m) => !leftKeys.has(`${m.kind}:${m.term_key}`)).map((m) => humanize(m.term_key));
        const parts = [
          left.length ? `Uses ${left.length === 1 ? 'a word' : 'words'} not declared for ${jurisdictionName(item.jurisdiction)} yet: ${left.map((m) => humanize(m.term_key)).join(', ')}.` : '',
          filed.length ? `Waiting for review: ${filed.join(', ')}.` : '',
        ];
        return parts.filter(Boolean).join(' ');
      }
      const concepts = strings(d.missing_concepts).map(humanize);
      return concepts.length ? `Rules can’t describe this yet: ${concepts.join(', ')}.` : 'Rules can’t describe what this law covers yet.';
    }
    case 'multi_rule_law': {
      const filed = filedCount(item);
      if (filed) return `${filed} ${filed === 1 ? 'rule' : 'rules'} filed; more to author.`;
      const n = typeof d.rule_count_estimate === 'number' ? d.rule_count_estimate : null;
      return n ? `This law needs about ${n} separate rules.` : 'This law needs more than one rule.';
    }
    case 'ungrounded': {
      const n = Array.isArray(d.unmatched) ? d.unmatched.length : 0;
      return n === 1 ? 'One part of the draft isn’t quoted from the law.' : `${n || 'Some'} parts of the draft aren’t quoted from the law.`;
    }
  }
}

function safeSourceUrl(url: string | null): string | null {
  return url && /^https?:\/\//i.test(url) ? url : null;
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function NeedsAuthoringLane({ canWrite, onBack }: { canWrite: boolean; onBack: () => void }) {
  const query = useQuery({
    queryKey: queryKeys.authoringItems(),
    queryFn: () => listAuthoringItems(),
    // Refresh while a background draft is running so the card updates by itself.
    refetchInterval: (q) => ((q.state.data?.items ?? []).some((i) => pendingSince(i)) ? 10_000 : false),
  });
  const [message, setMessage] = useState<{ n: number; text: string } | null>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const items = query.data?.items ?? [];

  // After an item leaves the list, put focus on the next card (or the lane heading).
  useEffect(() => {
    if (focusIndex === null || query.isFetching) return;
    const headings = listRef.current?.querySelectorAll<HTMLElement>('[data-card-heading]') ?? [];
    const target = headings.length ? headings[Math.min(focusIndex, headings.length - 1)] : headingRef.current;
    target?.focus();
    setFocusIndex(null);
  }, [focusIndex, query.isFetching, items.length]);

  const announce = (text: string) => setMessage((m) => ({ n: (m?.n ?? 0) + 1, text }));

  return (
    <section aria-labelledby="authoring-h" style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <h2 id="authoring-h" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
        Needs authoring
      </h2>
      {/* One persistent status region, mounted in every state. */}
      <div role="status" aria-live="polite">
        {message ? (
          <p key={message.n} style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-sm)' }}>
            {message.text}
          </p>
        ) : null}
      </div>
      {query.isLoading ? (
        <Spinner label="Loading laws that need authoring" />
      ) : query.error ? (
        <Alert variant="error">
          <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            Couldn’t load the laws that need authoring.
            <Button compact onClick={() => void query.refetch()}>Retry</Button>
          </span>
        </Alert>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nothing needs authoring"
          description="Every approved law has a draft rule or has been handled."
          action={<Button onClick={onBack}>Back to the queue</Button>}
        />
      ) : (
        <ol ref={listRef} style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
          {items.map((item, index) => (
            <li key={item.item_id}>
              <AuthoringCard
                item={item}
                canWrite={canWrite}
                primary={index === 0}
                onDone={(text, left) => {
                  announce(text);
                  if (left) setFocusIndex(index);
                }}
              />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function AuthoringCard({ item, canWrite, primary, onDone }: {
  item: RuleAuthoringItemView;
  canWrite: boolean;
  /** The lane's first card carries the lane's one filled primary. */
  primary: boolean;
  /** `left` = the item leaves the lane. */
  onDone: (text: string, left: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState<RuleAuthoringAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [concept, setConcept] = useState('');
  const [linked, setLinked] = useState<string[]>([]);
  const [termOpen, setTermOpen] = useState(false);
  const firstMissing = remainingTerms(item)[0];
  const formRef = useRef<HTMLFormElement>(null);
  const headingRef = useRef<HTMLSpanElement>(null);
  const law = item.legislation_short_title || item.legislation_title;
  const [main, ...others] = orderedActions(item);
  const idBase = `ai-${item.item_id}`;
  const since = pendingSince(item);
  const source = safeSourceUrl(item.source_url);

  // Focus the first control when a form opens.
  useEffect(() => {
    if (open) formRef.current?.querySelector<HTMLElement>('textarea, input, button[type="submit"]')?.focus();
  }, [open]);

  const openForm = (action: RuleAuthoringAction) => {
    setError(null);
    setSubmitted(false);
    setNote('');
    setText('');
    const termKeys = new Set(missingTerms(item).map((m) => m.term_key));
    setConcept(humanize(strings((item.detail as Record<string, unknown>).missing_concepts).filter((c) => !termKeys.has(c))[0] ?? ''));
    setLinked(item.linked_rule_ids);
    setOpen(action);
  };
  const closeForm = () => {
    setOpen(null);
    setError(null);
    setNote('');
    setText('');
    requestAnimationFrame(() => headingRef.current?.focus());
  };

  const run = async (action: RuleAuthoringAction) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const body =
        action === 'add_statute_text' ? { text }
        : action === 'request_vocabulary' ? { concept: toFieldName(concept), note: note.trim() || undefined }
        : action === 'author_rule' || action === 'mark_not_applicable' ? { note: note.trim() }
        : action === 'link_existing' ? { linked_rule_ids: linked }
        : {};
      const r = await actOnAuthoringItem(item.item_id, action, body);
      let msg: string;
      let left: boolean;
      if (RERUNS.has(action)) {
        const outcome = r.started === false ? r.outcome?.outcome : undefined;
        msg = outcome === 'proposed' ? `${law}: draft rule filed for review.`
          : outcome === 'escalated' ? `${law}: still needs authoring.`
          : outcome === 'skipped' ? `${law}: nothing new to draft.`
          : `${law}: drafting again.`;
        left = outcome === 'proposed';
      } else if (action === 'request_vocabulary') {
        msg = `${law}: field requested.`;
        left = false;
      } else {
        msg = `${law}: resolved.`;
        left = true;
      }
      setOpen(null);
      onDone(msg, left);
      await queryClient.invalidateQueries({ queryKey: ['authoring-items'] });
      await queryClient.invalidateQueries({ queryKey: ['queue'] });
      if (!left) requestAnimationFrame(() => headingRef.current?.focus());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const start = (action: RuleAuthoringAction) => {
    if (busy || (since && RERUNS.has(action))) return;
    // V104-G-12: "Propose the term" opens the term form, prefilled from the drafter's word.
    if (action === 'propose_term') {
      setError(null);
      setOpen(null);
      setTermOpen(true);
      return;
    }
    // V104-G-10: "Author the rule" opens the authoring form.
    if (action === 'author_rule') {
      navigate(`authoring/${encodeURIComponent(item.item_id)}`);
      return;
    }
    if (NEEDS_FORM.has(action)) openForm(action);
    else void run(action);
  };

  const textChars = text.trim().length;
  const fieldName = toFieldName(concept);
  const invalid: string | null =
    open === 'add_statute_text' ? (textChars >= MIN_TEXT ? null : `${(MIN_TEXT - textChars).toLocaleString()} more characters needed.`)
    : open === 'request_vocabulary' ? (fieldName.length >= 2 ? null : 'Describe it in a few words.')
    : open === 'author_rule' || open === 'mark_not_applicable' ? (note.trim().length >= MIN_NOTE ? null : `At least ${MIN_NOTE} characters.`)
    : open === 'link_existing' ? (linked.length > 0 ? null : 'Pick at least one rule.')
    : null;
  const showInvalid = submitted ? invalid : null;

  const filed = filedCount(item);
  const actionButton = (a: RuleAuthoringAction, variant: 'primary' | 'secondary' | 'ghost') => {
    const locked = busy || (!!since && RERUNS.has(a));
    return (
      <Button key={a} compact variant={variant} aria-disabled={locked || undefined} aria-describedby={`${idBase}-h`} onClick={() => start(a)}>
        {a === 'author_rule' && filed ? 'Continue authoring' : ACTION_LABEL[a]}
      </Button>
    );
  };
  /** The note form for a rule written elsewhere sits right after "Author the rule". */
  const markWritten = (
    <Button key="mark-written" compact variant="ghost" aria-describedby={`${idBase}-h`} onClick={() => !busy && openForm('author_rule')}>
      {MARK_WRITTEN}
    </Button>
  );
  const quiet = [...(main === 'author_rule' ? [markWritten] : []), ...others.flatMap((a) => (a === 'author_rule' ? [actionButton(a, 'ghost'), markWritten] : [actionButton(a, 'ghost')]))];

  return (
    <Card
      title={
        <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <span id={`${idBase}-h`} ref={headingRef} tabIndex={-1} data-card-heading>
            {law}
          </span>
          <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 'var(--font-size-sm)' }}>· {jurisdictionName(item.jurisdiction)}</span>
          <Badge>{item.state === 'blocked_vocabulary' ? 'Waiting for a rule field' : missingTerms(item).length ? 'Needs a law word' : REASON_LABEL[item.reason_code]}</Badge>
          {since ? <Badge tone="accent">Drafting since {fmtTime(since)}</Badge> : null}
        </span>
      }
      right={canWrite && main && !open && !termOpen ? actionButton(main, primary ? 'primary' : 'secondary') : null}
    >
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
        {describeItem(item)}
        {source ? (
          <>
            {' '}
            <a href={source} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
              Law source
            </a>
          </>
        ) : null}
      </p>
      {canWrite && quiet.length && !open && !termOpen ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
          {quiet}
        </div>
      ) : null}
      {busy && !open ? <Spinner label="Working" /> : null}
      {error ? <Alert variant="error">That didn’t save: {error}</Alert> : null}
      {termOpen ? (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <TermProposalForm
            itemId={item.item_id}
            jurisdictionLabel={jurisdictionName(item.jurisdiction)}
            kind={firstMissing?.kind ?? 'role'}
            chooseKind
            defaultWord={firstMissing?.term_key}
            defaultSourceUrl={source}
            onCancel={() => {
              setTermOpen(false);
              requestAnimationFrame(() => headingRef.current?.focus());
            }}
            onFiled={(term) => {
              setTermOpen(false);
              onDone(`${law}: “${term.label}” filed for review.`, false);
              void queryClient.invalidateQueries({ queryKey: ['authoring-items'] });
              void queryClient.invalidateQueries({ queryKey: ['queue'] });
              requestAnimationFrame(() => headingRef.current?.focus());
            }}
          />
        </div>
      ) : null}
      {open ? (
        <form
          ref={formRef}
          aria-label={`${open === 'author_rule' ? MARK_WRITTEN : ACTION_LABEL[open]}: ${law}`}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (!invalid) void run(open);
          }}
          style={{ display: 'grid', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}
        >
          {open === 'add_statute_text' ? (
            <>
              <Field id={`${idBase}-text`} label="Law text" hint={`Paste the text, or load a .txt file below. At least ${MIN_TEXT.toLocaleString()} characters (${textChars.toLocaleString()} so far).`} error={showInvalid} required>
                <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} />
              </Field>
              <Field id={`${idBase}-file`} label="Load a .txt file">
                <Input
                  type="file"
                  accept=".txt,text/plain"
                  onChange={(e) => {
                    const f = e.currentTarget.files?.[0];
                    if (!f) return;
                    if (f.type && f.type !== 'text/plain') {
                      setError('Only a plain-text (.txt) file can be loaded.');
                      return;
                    }
                    f.text().then(setText, () => setError('That file couldn’t be read.'));
                  }}
                />
              </Field>
            </>
          ) : null}
          {open === 'request_vocabulary' ? (
            <>
              <Field id={`${idBase}-concept`} label="What should rules be able to describe?" hint="A few words, e.g. deepfakes of real people." error={showInvalid} required>
                <Input value={concept} onChange={(e) => setConcept(e.target.value)} />
              </Field>
              <Field id={`${idBase}-vnote`} label="Note (optional)">
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            </>
          ) : null}
          {open === 'author_rule' ? (
            <Field id={`${idBase}-anote`} label="Where is the rule?" hint={`For example, the proposal you filed for it. At least ${MIN_NOTE} characters.`} error={showInvalid} required>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          ) : null}
          {open === 'mark_not_applicable' ? (
            <Field id={`${idBase}-nnote`} label="Why doesn’t this law need a rule?" hint={`At least ${MIN_NOTE} characters.`} error={showInvalid} required>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          ) : null}
          {open === 'link_existing' ? (
            <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-1)' }}>
              <legend style={{ color: 'var(--text)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>Rules that cover this law</legend>
              {item.linked_rules.map((r) => (
                <Checkbox
                  key={r.rule_id}
                  label={`${r.statute} · ${r.citation}`}
                  checked={linked.includes(r.rule_id)}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setLinked((cur) => (on ? [...cur, r.rule_id] : cur.filter((x) => x !== r.rule_id)));
                  }}
                />
              ))}
              {showInvalid ? <span role="alert" style={{ color: 'var(--critical)', fontSize: 'var(--font-size-sm)' }}>{showInvalid}</span> : null}
            </fieldset>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Button type="submit" compact variant="primary" aria-disabled={busy || undefined}>
              {busy ? 'Working…' : open === 'add_statute_text' ? 'Draft from this text' : open === 'author_rule' ? MARK_WRITTEN : ACTION_LABEL[open]}
            </Button>
            <Button compact variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}
